import { CreateStage } from './index';
import axios from 'axios';
import isEqualLodash from 'lodash/isEqual';
import { debounce, select, takeLatest } from 'redux-saga/effects';

import { ERROR_TYPES } from 'constants/errors';
import { DEFAULT_ERROR_MESSAGE } from 'constants/index';
import AppError from 'utils/errors/AppError';

import * as Constants from '../constants';
import * as Selectors from '../selectors';

const DEFAULT_CALLBACK = function* () {
  yield;
};
const PAGES_ACTIONS = Object.values(Constants.PAGES_ACTIONS);
const REQUIRED_OPTIONS = ['action', 'handler', 'name'];
const STAGES_ACTIONS = Object.values(Constants.STAGES_ACTIONS);

export class AbstractWatcher {
  called = false;
  options = {};
  state = {};

  constructor(options = {}) {
    for (const key of REQUIRED_OPTIONS) {
      if (options[key]) {
        continue;
      }

      throw new Error(`"${key}" is required`);
    }

    const {
      after = DEFAULT_CALLBACK,
      before = DEFAULT_CALLBACK,
      watch = DEFAULT_CALLBACK,
      ...rest
    } = options;

    this.options = { ...rest, after, before, watch };
  }

  static errorHandler(error) {
    let { message, type } = error ?? {};

    message ||= DEFAULT_ERROR_MESSAGE;
    type ||= Constants.ERROR_TYPES.FRONTEND;

    if (error instanceof AppError) {
      const { type: errorType } = error;

      if (errorType === ERROR_TYPES.HTTP_ERROR) {
        type = Constants.ERROR_TYPES.BACKEND;
      }
    } else if (axios.isAxiosError(error)) {
      type = Constants.ERROR_TYPES.BACKEND;
    }

    return { message, type };
  }

  static getStageName(stage) {
    return stage[Constants.ABSTRACT_WATCHER_NAME_SYMBOL];
  }

  static throwError({ code, description, message, type = ERROR_TYPES.HTTP_ERROR }) {
    throw new AppError(message, code, type, description);
  }

  *action(params) {
    const { after: afterOption, before: beforeOption, handler: handlerOption } = this.options;

    yield* beforeOption(params);
    yield* handlerOption(params);
    yield* afterOption(params);
  }

  *actionEffect(...args) {
    this.called = true;

    yield* this.action(...args);
  }

  *watchEffect({ callback, key, selector }) {
    if (!this.called) {
      return;
    }

    const current = yield select(selector);
    const previous = this.state[key];

    if (isEqualLodash(current, previous)) {
      return;
    }

    this.state[key] = structuredClone(current);

    yield callback({ current, previous });
  }

  watchSelfUpdate(callback = DEFAULT_CALLBACK) {
    const { name: nameOption } = this.options;

    const isStage = this instanceof CreateStage;

    const pattern = isStage ? STAGES_ACTIONS : PAGES_ACTIONS;
    const selector = isStage ? Selectors.selectStage(nameOption) : Selectors.selectPage(nameOption);

    return debounce(
      50,
      pattern,
      this.watchEffect.bind(this, { callback, key: `self.${nameOption}`, selector })
    );
  }

  watchStageUpdate(stage, callback = DEFAULT_CALLBACK) {
    const { [Constants.ABSTRACT_WATCHER_NAME_SYMBOL]: stageName } = stage;

    const selector = Selectors.selectStage(stageName);

    return debounce(
      50,
      STAGES_ACTIONS,
      this.watchEffect.bind(this, { callback, key: `stage.${stageName}`, selector })
    );
  }

  get watcher() {
    const { action: actionOption, name: nameOption, watch: watchOption } = this.options;

    const actionEffect = this.actionEffect.bind(this);
    const watchSelfUpdate = this.watchSelfUpdate.bind(this);
    const watchStageUpdate = this.watchStageUpdate.bind(this);

    function* watcher() {
      yield takeLatest(actionOption, actionEffect);
      yield* watchOption({ watchSelfUpdate, watchStageUpdate });
    }

    watcher[Constants.ABSTRACT_WATCHER_ACTION_SYMBOL] = actionOption;
    watcher[Constants.ABSTRACT_WATCHER_NAME_SYMBOL] = nameOption;

    return watcher;
  }
}
